import { useScheduleStore } from '@/stores/schedule';
import { computeTimeRangeEndDate, resolvePublishedCalendar } from '@/utils/CalendarEndDateCalculator';
import { prepareJsonBody } from '@/utils/httpCompression';

/**
 * Utility class for importing tasks and task sets to schedules
 */
export class ScheduleImporter {
  constructor(apiClient, settings) {
    this.apiClient = apiClient;
    this.settings = settings;
  }

  /**
   * Import tasks to a schedule
   * @param {Object} params - Import parameters
   * @param {string} params.scheduleId - Schedule ID to import to
   * @param {Array} params.tasks - Nested tasks array
   * @param {string} params.commitTitle - Commit title
   * @param {string} params.commitMessage - Commit message
   * @param {Object} params.taskSetsPayload - Task sets payload (optional)
   * @param {Array} params.keys - Lock keys to present (e.g. ['p6'] for P6
   *   imports, whose tasks are structure-locked with that key) (optional)
   * @param {boolean} params.preserveEndDates - Keep every endDate the tasks
   *   already carry instead of re-walking it from the duration. P6 imports
   *   set this: their ends are the P6 FinishDates (the commit stores endDate
   *   verbatim and the API's publish-time recalculation skips keyed tasks),
   *   so the walk would only move them off the source data. (optional)
   * @returns {Promise<void>}
   */
  async importTasks({ scheduleId, tasks, commitTitle, commitMessage, taskSetsPayload = {}, keys = [], preserveEndDates = false }) {
    const payload = {
      title: commitTitle,
      message: commitMessage,
      keys,
      tasks: this.applyCalculatedEndDates(this.stripRowIndex(tasks), { preserveEndDates })
    };

    try {
      // A large-schedule commit can be a 90+ MB JSON body — over the plan
      // upload limit at the CDN (413). Gzip it client-side when big enough;
      // the API's request decompression inflates it server-side.
      const { body, headers } = await prepareJsonBody(JSON.stringify(payload));
      await this.apiClient.post(
        `/api/${this.settings.projectCode}/schedules/${scheduleId}/commits`,
        body,
        null,
        false,
        headers
      );

      // Import task sets if provided
      if (Object.keys(taskSetsPayload).length > 0) {
        await this.importTaskSets({ taskSetsPayload, commitTitle, commitMessage });
      }
    } catch (error) {
      console.error('Error importing tasks:', error);
      throw error;
    }
  }

  /**
   * Import task sets to schedule
   * @param {Object} params - Import parameters
   * @param {Object} params.taskSetsPayload - Task sets payload
   * @param {string} params.commitTitle - Commit title
   * @param {string} params.commitMessage - Commit message
   * @returns {Promise<void>}
   */
  async importTaskSets({ taskSetsPayload, commitTitle, commitMessage }) {
    try {
      const promises = Object.entries(taskSetsPayload).map(async ([taskSetProfileId, data]) => {
        const payload = {
          title: commitTitle,
          message: commitMessage,
          deployments: data.deployments
        };

        // Deployment payloads scale with the schedule too — same gzip treatment
        // as the commit body.
        const { body, headers } = await prepareJsonBody(JSON.stringify(payload));
        return this.apiClient.patch(
          `/api/${this.settings.projectCode}/task-set-profiles/${taskSetProfileId}/deployments`,
          body,
          null,
          false,
          headers
        );
      });

      await Promise.all(promises);
    } catch (error) {
      console.error('Error importing task sets:', error);
      throw error;
    }
  }

  /**
   * Remove the rowIndex metadata that spreadsheet validation attaches to tasks so
   * it is not sent to the API. Returns a new tree; the input is left untouched.
   * @param {Array} tasks - Nested tasks array
   * @returns {Array} Tasks without rowIndex
   */
  stripRowIndex(tasks) {
    return (tasks || []).map((task) => {
      const cleaned = { ...task };
      delete cleaned.rowIndex;
      if (Array.isArray(task.children)) {
        cleaned.children = this.stripRowIndex(task.children);
      }
      return cleaned;
    });
  }

  /**
   * Fill timeRanges[].endDate from startDate + duration + calendar before the
   * commit is posted. The commit endpoint stores endDate verbatim and never
   * derives it (the API only recomputes end dates on calendar publish), so the
   * client must submit it. Mirrors the publish-time rules per template type:
   * 'calendarTracking' and 'dependencyTracking' are always recomputed,
   * 'timeTracking' (actuals) is only filled when missing, 'none' is untouched.
   * Returns a new tree with cloned timeRanges; unresolvable ranges (unknown
   * calendar, no working time, missing inputs) are left as-is, except that a
   * stale endDate earlier than the new startDate is dropped so the commit does
   * not fail the API's StartDate <= EndDate validation.
   * @param {Array} tasks - Nested tasks array
   * @param {{preserveEndDates?: boolean}} [options] - see importTasks
   * @returns {Array} Tasks with calculated end dates
   */
  applyCalculatedEndDates(tasks, { preserveEndDates = false } = {}) {
    const calendars = useScheduleStore().getCalendars || [];

    const applyToTask = (task) => {
      const cloned = { ...task };
      if (Array.isArray(task.timeRanges)) {
        cloned.timeRanges = task.timeRanges.map(tr => this.applyEndDateToTimeRange(tr, calendars, preserveEndDates));
      }
      if (Array.isArray(task.children)) {
        cloned.children = task.children.map(applyToTask);
      }
      return cloned;
    };

    return (tasks || []).map(applyToTask);
  }

  applyEndDateToTimeRange(timeRange, calendars, preserveEndDates = false) {
    const type = timeRange?.timeRangeTemplateType;
    const isRecalculated = type === 'calendarTracking' || type === 'dependencyTracking';
    const isFillIfMissing = type === 'timeTracking';

    if (!isRecalculated && !(isFillIfMissing && !timeRange.endDate)) {
      return timeRange;
    }

    // A source-supplied end (P6 FinishDate) is committed as-is; only an
    // inverted leftover still falls through to the cleanup below.
    if (preserveEndDates && timeRange.endDate && timeRange.startDate
      && Date.parse(timeRange.endDate) >= Date.parse(timeRange.startDate)) {
      return timeRange;
    }

    const calendar = resolvePublishedCalendar(calendars, timeRange);
    const endDate = computeTimeRangeEndDate(timeRange, calendar);
    if (endDate) {
      return { ...timeRange, endDate };
    }

    // Could not compute — drop an inverted leftover endDate, keep anything else.
    if (timeRange.endDate && timeRange.startDate
      && Date.parse(timeRange.endDate) < Date.parse(timeRange.startDate)) {
      const { endDate: _stale, ...rest } = timeRange; // eslint-disable-line no-unused-vars
      return rest;
    }
    return timeRange;
  }
}
